/**
 * 1.6 — the strokes on the signature pad, kept so the last one can be taken
 * back.
 *
 * A canvas remembers pixels, not strokes: once a line is on it there is no
 * taking that line off again. So the pad keeps every stroke as the points the
 * finger went through, and taking one back is drawing the rest again on a
 * cleared canvas.
 *
 * Its own module so it can be tested without a browser, and so the pad draws a
 * live stroke with the same two calls that draw it again: a line stroked once
 * is a lighter picture than the same line stroked again at every move, which is
 * how the pad has always drawn, and the strokes left after an undo must not
 * change under the finger that pressed it.
 */

/** A point on the pad, in its own CSS pixels from its top-left corner. */
export interface PadPoint {
  x: number;
  y: number;
}

/** One stroke: where the pen came down, then every place it moved to before it lifted. */
export type Stroke = readonly PadPoint[] & { readonly color?: string; readonly width?: number };

/** The part of a canvas the pen draws with. */
export type Pen = Pick<CanvasRenderingContext2D, 'beginPath' | 'moveTo' | 'lineTo' | 'stroke'> & Partial<Pick<CanvasRenderingContext2D, 'strokeStyle' | 'lineWidth'>>;

/** The pen coming down: a new line starts where it touched. */
export function penDown(pen: Pen, at: PadPoint): void {
  pen.beginPath();
  pen.moveTo(at.x, at.y);
}

/** The pen moving: the line is drawn on to where it went. */
export function penMove(pen: Pen, to: PadPoint): void {
  pen.lineTo(to.x, to.y);
  pen.stroke();
}

/** `strokes` drawn again, oldest first, each exactly as the pen drew it. */
export function paint(pen: Pen, strokes: readonly Stroke[]): void {
  for (const stroke of strokes) {
    if (stroke.color) pen.strokeStyle = stroke.color;
    if (stroke.width) pen.lineWidth = stroke.width;
    const [down, ...moves] = stroke;
    if (!down) continue;
    penDown(pen, down);
    for (const point of moves) penMove(pen, point);
  }
}

/**
 * The strokes once the pen has lifted off `stroke`. A press that never moved
 * put no ink down, so it is not kept: taking it back would change nothing on
 * the pad, and the button would seem not to work.
 */
export function lift(strokes: readonly Stroke[], stroke: Stroke): Stroke[] {
  return stroke.length > 1 ? [...strokes, stroke] : [...strokes];
}

/** The strokes with the last one taken back. */
export function takeBack(strokes: readonly Stroke[]): Stroke[] {
  return strokes.slice(0, -1);
}
